import * as R from 'ramda';
import { Onfido } from 'onfido-sdk-ui';
import { CancellableResult } from '../../common/result';
import { TechnicalError } from '../../common/errors';
import Constants from './constants';

import {
  DEFAULT_SUPPORTED_DOCUMENT_TYPES,
  DocumentCapture,
  DocumentCaptureResult,
  DocumentType,
} from './documentCapture';

export default class OnfidoDocumentCapture implements DocumentCapture {
  // The current onfido version does not expose types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onfido: any | null = null;

  private darkMode: boolean = false;

  setDarkMode(darkModeEnabled = false): void {
    this.darkMode = darkModeEnabled;
  }

  async capture(token: string, supportedDocumentTypes?: DocumentType[]): Promise<DocumentCaptureResult> {
    const useSupportedTypes: DocumentType[] = supportedDocumentTypes || DEFAULT_SUPPORTED_DOCUMENT_TYPES;
    const docTypesObject = R.reduce(
      (acc: Partial<Record<DocumentType, boolean>>, doctype: DocumentType) => ({
        ...acc,
        [doctype]: useSupportedTypes.includes(doctype),
      }),
      {},
      Object.values(DocumentType),
    );
    return new Promise((resolve) => {
      this.onfido = Onfido.init({
        token,
        containerId: Constants.DocumentCaptureContainerId,
        customUI: Constants.getOnfidoCustomization(this.darkMode),
        steps: [
          {
            type: 'document',
            options: {
              // Allow the user to only capture their document using the camera on their mobile device
              // We only allow document upload on e2e, local and dev stages in order to allow e2e tests to work
              forceCrossDevice: !['e2e', 'local', 'dev'].includes(process.env.REACT_APP_STAGE || 'unknown'),
              documentTypes: docTypesObject,
            },
          },
        ],
        // The current onfido version does not expose types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onComplete(data: any) {
          resolve(OnfidoDocumentCapture.mapSuccessResult(data));
        },
        // The current onfido version does not expose types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError(error: any) {
          if (error.type === 'user_consent_denied') {
            resolve(CancellableResult.cancelled());
            return;
          }
          resolve(OnfidoDocumentCapture.mapErrorResult(error));
        },
        version: '14.34.0',
      });
    });
  }

  // The current onfido version does not expose types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static mapSuccessResult(response: any): DocumentCaptureResult {
    const document = response.document_front;
    if (!document) {
      return CancellableResult.failed(
        new TechnicalError(`Unexpected empty document front response from onfido: ${JSON.stringify(response)}`),
      );
    }

    return CancellableResult.successful({
      id: document.id,
      documentType: document.type,
    });
  }

  // The current onfido version does not expose types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static mapErrorResult(error: any): DocumentCaptureResult {
    return CancellableResult.failed(
      new TechnicalError(`Unexpected error result from onfido: ${JSON.stringify(error)}`),
    );
  }

  async cleanup(): Promise<void> {
    await this.onfido?.tearDown();
    this.onfido = null;
  }
}
